import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import { queryClient } from '@/lib/utils/queryClient';
import { fallback } from '@/lib/utils/zodFallback';
import { AdminOriginsTable } from '../-components/AdminOriginsTable';
import { CreateOriginDialog } from '../-components/CreateOriginDialog';
import { OriginDetailSheet } from '../-components/OriginDetailSheet';
import { adminOriginsQueryOptions } from '../-hooks/useAdminOrigins';
import { useAdminOriginsPage } from '../-hooks/useAdminOriginsPage';

const originsSearch = z.object({
  page: fallback(z.number().int().min(1), 1),
  limit: fallback(z.number().int().min(10).max(200), 50),
  sort: z
    .string()
    .transform(v => (/^\w+:(ASC|DESC)$/.test(v) ? v : undefined))
    .optional(),
  filters: z
    .union([
      z.string().transform(v => {
        try {
          return JSON.parse(v) as Record<string, string[]>;
        } catch {
          return undefined;
        }
      }),
      z.record(z.string(), z.array(z.string())),
    ])
    .optional(),
});

export const Route = createFileRoute('/management/origins/(pages)/')({
  validateSearch: originsSearch,
  loaderDeps: ({ search }) => ({
    page: search.page ?? 1,
    limit: search.limit ?? 50,
    ...(search.sort !== undefined ? { sort: search.sort } : {}),
    filters: search.filters ?? {},
  }),
  // Swallowed: a rejected loader hands the route to the router's error
  // boundary, and the table already reports a failed load as a banner.
  loader: ({ deps }) =>
    queryClient
      .ensureQueryData(adminOriginsQueryOptions(deps))
      .catch(() => undefined),
  component: OriginsPage,
});

function OriginsPage() {
  const page = useAdminOriginsPage();

  return (
    <PageContainer>
      <AdminOriginsTable page={page} />
      <CreateOriginDialog page={page} />
      <OriginDetailSheet page={page} />
    </PageContainer>
  );
}
